import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {Observable} from 'rxjs';
import {PaymentIntent, StripeCardElementOptions, StripeElementsOptions} from '@stripe/stripe-js';
import {finalize, switchMap, take} from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {GlobalConstantsComponent} from '../../../global-constants/global-constants.component';
import {FormBuilder, FormControl, FormGroup, FormRecord, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {NgxStripeModule, StripeCardNumberComponent, StripeService} from 'ngx-stripe';
import {HubspotService} from '../../../../services/hubspot.service';
import {UserService} from '../../../../services/user-services/user.service';
import {Router} from '@angular/router';
import {AlertService} from '../../../../services/alert.service';
import {DocumentsSharedService} from '../../../../services/documents-shared.service';
import {AuthenticationService} from 'src/app/services/authentication.service';
import {TranslateModule} from '@ngx-translate/core';
import {PaypalButtonComponent} from '../../ux/buttons/paypal-button/paypal-button.component';
import {NgClass, NgFor, NgIf} from '@angular/common';
import {NgxIntlTelInputModule} from '@justin-s/ngx-intl-tel-input';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {IconComponent} from '../../icons-svgs/icons-svgs.component';
import {MatTabsModule} from '@angular/material/tabs';
import { LocalSettingsService } from 'src/app/services/local-settings.service';

@Component({
  selector: 'app-global-payment-form',
  templateUrl: './global-payment-form.component.html',
  styleUrls: ['./global-payment-form.component.scss'],
  standalone: true,
  imports: [MatTabsModule, IconComponent, MatFormFieldModule, MatInputModule, FormsModule, ReactiveFormsModule, NgxIntlTelInputModule, NgIf, NgxStripeModule, NgFor, NgClass, PaypalButtonComponent, TranslateModule]
})
export class GlobalPaymentFormComponent implements OnInit {

  /**
   * @service : the name of the service the user pay for. Use for payment.
   * @price : the amount the user have to pay. Use for payment.
   * @text : Translate key for the subtitle.
   * @nextUrl : use when payment is done, to redirect
   * @pdfKey : if a PDF needs to be generate after payment, assign the doc key to it
   */
  @Input() public service: string;
  @Input() public price: number;
  @Input() text: string;
  @Input() public nextUrl: string = 'discover';
  @Input() public pdfKey: string = null;
  @Input() public promoCode: string = null;
  @Input() public process;
  @Input() public booking;
  @Input() public room;

  @Output() private sendLoadingStatus: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() private hasButtonDisplay: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() private sendInfos: EventEmitter<any> = new EventEmitter<any>();

  private apiUrl = GlobalConstantsComponent.apiURL;
  stripeTest: FormRecord;
  paymentForm: FormGroup = new FormGroup({
    cardOwner: new FormControl<string | null>(null, {nonNullable: true}),
  });
  isPaymentFormSubmitted: boolean = false;
  paySuccess: boolean = false;
  tabNumber: number = 0;

  elementsOptions: StripeElementsOptions = {
    locale: 'fr',
  };
  stripeError = null;
  promoCodeAmount = null;
  @ViewChild(StripeCardNumberComponent) private card: StripeCardNumberComponent;
  cardOptions: StripeCardElementOptions = {
    hideIcon: true,
    disableLink: true,
    style: {
      base: {
        color: '#000000',
        fontWeight: '400',
        fontFamily: '"Hind Medium", sans-serif',
        fontSize: '14px',
        lineHeight: '25px',
      },
    },
  };
  private currentUser: any;
  creditCardArray: string[] = ['Visa', 'MasterCard','Amex','Maestro', 'Discover', 'DinerClub','JCB' ];
  isLoading:boolean = true;
  currentLanguage: string = 'en';

  constructor(
    private http: HttpClient,
    private stripeService: StripeService,
    private hubspotService: HubspotService,
    private formBuilder: FormBuilder,
    private userService: UserService,
    private router: Router,
    private alertService: AlertService,
    private documentService: DocumentsSharedService,
    private authenticationService: AuthenticationService,
    private localSettings: LocalSettingsService,
  ) { }

  ngOnInit(): void {
    this.localSettings.language$.subscribe(
      (language: string) => {
        this.currentLanguage = language;
      }
    );
    this.userService.getDatas().pipe(take(1)).subscribe( (data) => {
      this.currentUser = data;
      this.stripeTest = this.formBuilder.group({
        name: [this.currentUser.firstname + ' ' + this.currentUser.lastname, [Validators.required]],
        amount: [this.price, [Validators.required, Validators.pattern(/\d+/)]],
      });
      this.isLoading = false;
    });
  }

  private createPaymentIntent(amount: number, userMail: string, service: string, promoCode, promoCodeAmount): Observable<PaymentIntent> {
    const httpOptions: object = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.authenticationService.currentUserValue.token}`
      }),
      reportProgress: true,
    }

    return this.http.post<PaymentIntent>(
      `${this.apiUrl}create-payment-intent`,
      {amount, userMail, service,promoCode, promoCodeAmount},httpOptions
    );
  }


  pay() {
    this.isPaymentFormSubmitted = true;
    if (this.service !== 'ACCOMMODATION') {
      if (this.stripeTest.valid && this.paymentForm.valid) {
        this.sendLoadingStatus.emit(true);
        if(!this.promoCode){
          this.promoCode = this.userService.getFromLocal('promoCode');
        }
        if(!this.promoCodeAmount){
          this.promoCodeAmount = this.userService.getFromLocal('promoCodeAmount');
        }
        this.createPaymentIntent(this.price,  this.currentUser.email, this.service, this.promoCode, this.promoCodeAmount)
          .pipe(
            switchMap((pi: any) =>
              this.stripeService.confirmCardPayment(pi.intent.client_secret, {
                payment_method: {
                  card: this.card.element,
                  billing_details: {
                    name: this.stripeTest.get('name').value,
                  },
                },
              })
            )
          )
          .subscribe((result) => {
            if (result.error) {
              // Show error to your customer (e.g., insufficient funds)
              const date = new Date();
              this.stripeError = {code: result.error.code, message: result.error.message};
              this.hubspotService.updateHubspot({
                  date_page_paiement: Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
                  failed_payment_api: result.error.message,
                  methode_de_paiement: 'Stripe'
                }).subscribe();

              this.alertService.openSnackBar(result.error.message, 'snack-error');
              this.sendLoadingStatus.emit(false);
            } else {
              if (result.paymentIntent.status === 'succeeded' || result.paymentIntent.status === 'requires_capture') {
                (async () => {
                  if(!this.promoCode){
                    this.promoCode = this.userService.getFromLocal('promoCode');
                  }
                  this.userService.createPayout({
                    service: this.service,
                    amount: this.price.toFixed(2),
                    payment: result.paymentIntent,
                    transaction_mode: 'stripe',
                    promo_code: this.promoCode
                  }).subscribe(
                    (data: any) => {
                      this.paySuccess = true;
                      if (this.pdfKey) {
                        this.documentService.genPdf(this.pdfKey)
                          .pipe(
                            take(1),
                            finalize( () => {this.router.navigate([this.currentLanguage+'/'+this.nextUrl]) } )
                          )
                          .subscribe();
                      } else {
                        this.router.navigate([this.currentLanguage+'/'+this.nextUrl]);
                      }
                    }
                  );

                })();
              }
            }
          });
      }
    } else {
      this.sendInfos.emit({card: this.card, valid: this.paymentForm.valid});
    }

  }

  public changeTab(e) {
    this.isPaymentFormSubmitted = false;
    this.stripeError = null;
    this.tabNumber = e.index;
    if (e.index == 1) {
      this.hasButtonDisplay.emit(false);
    } else {
      this.hasButtonDisplay.emit(true);
    }
  }

}
